'use client';

// A ChatGPT-style table card that surfaces SOURCE data (shortlist fits, campaigns,
// contacts, or a brand fiche) inside the Master chat. Read-only: all edits are
// chat-driven. It shows only the TOP rows; when there are more, a "See more in Source"
// footer leaves the Master session and opens the full list in the Source tool. It
// prefers LIVE rows re-read from the kernel inventory and falls back to the static
// `data` snapshot embedded in the server-durable chat marker.
import React, { useMemo } from 'react';
import { useKernelStore } from '@/kernel/kernelStores';
import { useKernel } from '@/kernel/Kernel';
import type { InventoryEntityType, EntityType } from '#types/types';
import { buildSourceSlice, type SourceCardKind, type SourceCardScope } from '@/components/source/sourceTableSlice';
import { type TableSlice } from '@/components/tables/analystTableSlice';
import { ChatTableCardShell } from '@/components/chat/ChatTableCardShell';
import { ArrowRight } from '@/components/ui/lucide-memo';

interface ChatSourceTableCardProps {
  kind: SourceCardKind;
  scope?: SourceCardScope;
  /** Inventory registry to re-read live (e.g. 'Lead', 'PortfolioFit', 'Contact', 'Brand', 'SourcingCampaign'). */
  entityType?: string;
  rowIds?: string[];
  /** Column subset the command chose (so the live rebuild matches the snapshot). */
  columns?: string[];
  /** Full server-side count; when > shown rows, the footer offers to open Source. */
  total?: number;
  /** Entity to open in the Source tool for the full list (Portfolio / Property) + its id. */
  navEntityType?: string;
  navEntityId?: string;
  title?: string;
  /** Server-durable snapshot embedded in the chat marker; used when the live rows aren't in this browser. */
  data?: TableSlice | null;
}

// Static style at module scope, like ChatTableCardShell: no fresh object reference
// handed to the DOM on a re-render.
const UNAVAILABLE_STYLE: React.CSSProperties = {
  margin: '10px 0',
  borderRadius: 12,
  border: '1px solid #2E2E2E',
  padding: '14px 16px',
  color: '#8E8E8E',
  fontSize: 13,
};

export function ChatSourceTableCard({
  kind,
  scope,
  entityType,
  rowIds,
  columns,
  total,
  navEntityType,
  navEntityId,
  title,
  data,
}: ChatSourceTableCardProps) {
  const { kernel } = useKernel();
  const registry = useKernelStore((s) =>
    entityType ? (s.inventory[entityType as InventoryEntityType]?.registry as Record<string, unknown> | undefined) : undefined
  );

  const columnsKey = columns ? columns.join(',') : '';
  const liveGrid = useMemo(() => {
    if (!registry || !rowIds || rowIds.length === 0) return null;
    const rows = rowIds.map((id) => registry[id]).filter(Boolean);
    if (rows.length !== rowIds.length) return null;
    return buildSourceSlice(kind, scope, rows, columns);
  }, [registry, rowIds, kind, scope, columnsKey]);

  const grid: TableSlice | null = liveGrid ?? (data && data.header ? data : null);

  if (!grid) {
    return (
      <div style={UNAVAILABLE_STYLE}>This data is no longer available.</div>
    );
  }

  const shown = grid.rows.length;
  const canSeeMore =
    typeof total === 'number' && total > shown && !!navEntityId && !!navEntityType;

  const openInSource = () => {
    const k = kernel.current;
    if (!k || !navEntityId || !navEntityType) return;
    // Leave the Master session first, then open the full list in the Source tool.
    k.clearActiveChatSession?.();
    void k.focusEntityUI?.(navEntityType as EntityType, navEntityId, 'ORCHESTRATE_SOURCING');
  };

  const footer = canSeeMore ? (
    <button
      type="button"
      onClick={openInSource}
      className="flex w-full items-center justify-center gap-1.5 border-t border-[#2A2A2A] px-3 py-2.5 text-[13px] text-[#B5B5B5] transition-colors hover:bg-[#242424] hover:text-white"
    >
      See all {total} in Source
      <ArrowRight size={14} strokeWidth={2} />
    </button>
  ) : null;

  return <ChatTableCardShell grid={grid} title={title} dropEmptyCols footer={footer} />;
}
